(function () {
  'use strict';

  var OVERLAY_ID = 'wraith-game-overlay';
  var RENDER_W = 220;
  var RENDER_H = 132;
  var HORIZON = 48; // sky rows, also = sky texture height
  var FOV = Math.PI / 3;
  var MOVE_SPEED = 2.6;    // world units / second
  var TURN_SPEED = 2.0;    // radians / second (arrow keys)
  var DRAG_SENSITIVITY = 0.0042;
  var SKY_TEX_W = 1024;
  var CLOUD_DRIFT_SPEED = 0.012; // texture-widths per second
  var TEX = 128;           // grass/stain texture size, power of two

  var state = null;
  var rafId = null;
  var skyTexCanvas = null;
  var grassTexData = null;

  // ---------- seeded RNG so the field looks the same every visit ----------
  function mulberry32(seed) {
    return function () {
      seed |= 0; seed = (seed + 0x6D2B79F5) | 0;
      var t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  // ---------- tileable grass + stain floor texture (from grass-walk) ----------
  function buildGrassTexture() {
    var rnd = mulberry32(1337);
    var canvas = document.createElement('canvas');
    canvas.width = TEX; canvas.height = TEX;
    var ctx = canvas.getContext('2d');

    var block = 2;
    for (var y = 0; y < TEX; y += block) {
      for (var x = 0; x < TEX; x += block) {
        var g = 60 + Math.floor(rnd() * 40);
        var variance = Math.floor(rnd() * 18) - 9;
        var r = 28 + variance * 0.4;
        var b = 26 + variance * 0.3;
        ctx.fillStyle = 'rgb(' + Math.max(0, r | 0) + ',' + g + ',' + Math.max(0, b | 0) + ')';
        ctx.fillRect(x, y, block, block);
      }
    }

    function stampWrapped(drawFn, cx, cy, radius) {
      for (var oy = -1; oy <= 1; oy++) {
        for (var ox = -1; ox <= 1; ox++) {
          var sx = cx + ox * TEX, sy = cy + oy * TEX;
          if (sx > -radius * 2 && sx < TEX + radius * 2 && sy > -radius * 2 && sy < TEX + radius * 2) {
            drawFn(sx, sy);
          }
        }
      }
    }

    function bloodBlot(cx, cy, r) {
      stampWrapped(function (sx, sy) {
        var blobs = 5 + Math.floor(rnd() * 5);
        for (var i = 0; i < blobs; i++) {
          var ang = rnd() * Math.PI * 2;
          var dist = rnd() * r * 0.6;
          var bx = sx + Math.cos(ang) * dist;
          var by = sy + Math.sin(ang) * dist;
          var br = r * 0.35 + rnd() * r * 0.45;
          var dark = rnd() < 0.5;
          ctx.fillStyle = dark
            ? 'rgba(' + (60 + rnd() * 20 | 0) + ',' + (4 + rnd() * 4 | 0) + ',' + (6 + rnd() * 4 | 0) + ',' + (0.75 + rnd() * 0.2) + ')'
            : 'rgba(' + (110 + rnd() * 40 | 0) + ',' + (8 + rnd() * 8 | 0) + ',' + (10 + rnd() * 8 | 0) + ',' + (0.7 + rnd() * 0.2) + ')';
          var steps = 6;
          ctx.beginPath();
          for (var s = 0; s < steps; s++) {
            var a = (s / steps) * Math.PI * 2;
            var rr = br * (0.6 + rnd() * 0.6);
            var px = bx + Math.cos(a) * rr, py = by + Math.sin(a) * rr;
            if (s === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
          }
          ctx.closePath();
          ctx.fill();
        }
        for (var j = 0; j < r; j++) {
          var a2 = rnd() * Math.PI * 2;
          var d = r * 0.6 + rnd() * r * 0.8;
          ctx.fillStyle = 'rgba(50,3,5,' + (0.5 + rnd() * 0.3) + ')';
          ctx.fillRect((sx + Math.cos(a2) * d) | 0, (sy + Math.sin(a2) * d) | 0, block, block);
        }
      }, cx, cy, r);
    }

    var stainCount = 9;
    for (var i = 0; i < stainCount; i++) {
      bloodBlot(rnd() * TEX, rnd() * TEX, 6 + rnd() * 9);
    }

    return ctx.getImageData(0, 0, TEX, TEX).data;
  }

  function sampleGrass(tx, ty) {
    tx &= (TEX - 1); ty &= (TEX - 1);
    var idx = (ty * TEX + tx) * 4;
    return [grassTexData[idx], grassTexData[idx + 1], grassTexData[idx + 2]];
  }

  // ---------- cylindrical sky with layered drifting clouds ----------
  function buildSkyTexture() {
    var c = document.createElement('canvas');
    c.width = SKY_TEX_W;
    c.height = HORIZON;
    var ctx = c.getContext('2d');

    var grad = ctx.createLinearGradient(0, 0, 0, HORIZON);
    grad.addColorStop(0, '#2a2f2b');
    grad.addColorStop(0.55, '#3c433c');
    grad.addColorStop(1, '#565f52');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, SKY_TEX_W, HORIZON);

    // layered cloud puffs, several wavelengths so the sky doesn't look like a single repeat
    var layers = [
      { period: 128, amp: 14, base: 6, alpha: 0.35, step: 4 },
      { period: 211, amp: 10, base: 10, alpha: 0.22, step: 5 },
      { period: 337, amp: 7, base: 4, alpha: 0.16, step: 6 }
    ];
    layers.forEach(function (layer) {
      ctx.fillStyle = 'rgba(120,128,116,' + layer.alpha + ')';
      for (var px = 0; px < SKY_TEX_W; px += layer.step) {
        var local = px % layer.period;
        var puff = Math.sin(local / layer.period * Math.PI * 2) * 0.5 + 0.5;
        var h = Math.floor(puff * layer.amp);
        if (h > 2) ctx.fillRect(px, layer.base, layer.step, h);
      }
    });

    return c;
  }

  // ---------- overlay DOM ----------
  function createOverlay() {
    var overlay = document.createElement('div');
    overlay.id = OVERLAY_ID;
    overlay.className = 'wraith-game-overlay';
    overlay.innerHTML =
      '<canvas class="wraith-game-canvas" width="' + RENDER_W + '" height="' + RENDER_H + '"></canvas>' +
      '<div class="wraith-game-hud">' +
        '<div class="wraith-game-hud__hint">wasd to walk &middot; drag to look &middot; esc or leave</div>' +
        '<button type="button" class="wraith-game-close">leave</button>' +
      '</div>' +
      '<div class="wraith-game-touch">' +
        '<div class="wraith-game-touch__group">' +
          '<button type="button" data-act="left">&#8634;</button>' +
          '<button type="button" data-act="right">&#8635;</button>' +
        '</div>' +
        '<div class="wraith-game-touch__group">' +
          '<button type="button" data-act="back">&#8595;</button>' +
          '<button type="button" data-act="forward">&#8593;</button>' +
        '</div>' +
      '</div>';
    document.body.appendChild(overlay);
    return overlay;
  }

  function getOverlay() {
    return document.getElementById(OVERLAY_ID) || createOverlay();
  }

  // ---------- render ----------
  function render(ctx, imgData, player, skyData, cloudDrift) {
    var data = imgData.data;
    var dirX = Math.cos(player.a);
    var dirY = Math.sin(player.a);
    var planeMag = Math.tan(FOV / 2);
    var planeX = -dirY * planeMag;
    var planeY = dirX * planeMag;

    var rayDirX0 = dirX - planeX, rayDirY0 = dirY - planeY;
    var rayDirX1 = dirX + planeX, rayDirY1 = dirY + planeY;

    // sky: cylindrical, wraps with facing angle, and drifts slowly on its own over time
    for (var y = 0; y < HORIZON; y++) {
      var rowOff = y * SKY_TEX_W * 4;
      for (var x = 0; x < RENDER_W; x++) {
        var viewAngle = player.a + (x / RENDER_W - 0.5) * FOV;
        var u = ((viewAngle / (Math.PI * 2)) + cloudDrift) % 1;
        if (u < 0) u += 1;
        var sx = Math.floor(u * (SKY_TEX_W - 1));
        var idx = rowOff + sx * 4;
        var p = (y * RENDER_W + x) * 4;
        data[p] = skyData[idx];
        data[p + 1] = skyData[idx + 1];
        data[p + 2] = skyData[idx + 2];
        data[p + 3] = 255;
      }
    }

    // floor: textured, fogged toward the horizon
    var camHeight = 0.62 * RENDER_H;
    for (var row = HORIZON; row < RENDER_H; row++) {
      var pRow = row - HORIZON;
      if (pRow <= 0) continue;
      var rowDistance = camHeight / pRow;

      var floorStepX = rowDistance * (rayDirX1 - rayDirX0) / RENDER_W;
      var floorStepY = rowDistance * (rayDirY1 - rayDirY0) / RENDER_W;
      var floorX = player.x + rowDistance * rayDirX0;
      var floorY = player.z + rowDistance * rayDirY0;

      var fog = Math.min(1, rowDistance / 16);
      var rowBase = row * RENDER_W * 4;

      for (var col = 0; col < RENDER_W; col++) {
        var cellX = Math.floor(floorX), cellY = Math.floor(floorY);
        var tx = Math.floor(TEX * (floorX - cellX)) & (TEX - 1);
        var ty = Math.floor(TEX * (floorY - cellY)) & (TEX - 1);
        floorX += floorStepX;
        floorY += floorStepY;

        var c = sampleGrass(tx, ty);
        var r = c[0] + (33 - c[0]) * fog;
        var g = c[1] + (35 - c[1]) * fog;
        var b = c[2] + (34 - c[2]) * fog;

        var pi = rowBase + col * 4;
        data[pi] = r; data[pi + 1] = g; data[pi + 2] = b; data[pi + 3] = 255;
      }
    }

    ctx.putImageData(imgData, 0, 0);
  }

  // ---------- loop ----------
  function loop(ts) {
    if (!state || !state.running) return;
    var dt = state.lastTs ? (ts - state.lastTs) / 1000 : 0;
    state.lastTs = ts;
    dt = Math.min(dt, 0.1);

    var turn = (state.keys.right ? 1 : 0) - (state.keys.left ? 1 : 0);
    state.player.a += turn * TURN_SPEED * dt;

    var dirX = Math.cos(state.player.a), dirY = Math.sin(state.player.a);
    var rightX = Math.cos(state.player.a + Math.PI / 2), rightY = Math.sin(state.player.a + Math.PI / 2);

    var mx = 0, my = 0, moving = false;
    if (state.keys.forward) { mx += dirX; my += dirY; moving = true; }
    if (state.keys.back) { mx -= dirX; my -= dirY; moving = true; }
    if (state.keys.strafeLeft) { mx -= rightX; my -= rightY; moving = true; }
    if (state.keys.strafeRight) { mx += rightX; my += rightY; moving = true; }

    if (moving) {
      var len = Math.hypot(mx, my) || 1;
      state.player.x += (mx / len) * MOVE_SPEED * dt;
      state.player.z += (my / len) * MOVE_SPEED * dt;
    }

    state.cloudDrift += CLOUD_DRIFT_SPEED * dt;

    render(state.ctx, state.imgData, state.player, state.skyData, state.cloudDrift);
    rafId = requestAnimationFrame(loop);
  }

  // ---------- input ----------
  function setKey(st, act, val) {
    if (act === 'forward') st.keys.forward = val;
    if (act === 'back') st.keys.back = val;
    if (act === 'left') st.keys.left = val;       // turn (arrow keys)
    if (act === 'right') st.keys.right = val;     // turn (arrow keys)
    if (act === 'strafeLeft') st.keys.strafeLeft = val;
    if (act === 'strafeRight') st.keys.strafeRight = val;
  }

  function hideHint(overlay) {
    var hint = overlay.querySelector('.wraith-game-hud__hint');
    if (hint) hint.classList.add('is-hidden');
  }

  function onKeyDown(e) {
    if (!state) return;
    if (e.key === 'Escape') { closeGame(); return; }
    var k = e.key.toLowerCase();
    if (e.key === 'ArrowUp' || k === 'w') setKey(state, 'forward', true);
    if (e.key === 'ArrowDown' || k === 's') setKey(state, 'back', true);
    if (e.key === 'ArrowLeft') setKey(state, 'left', true);
    if (e.key === 'ArrowRight') setKey(state, 'right', true);
    if (k === 'a') setKey(state, 'strafeLeft', true);
    if (k === 'd') setKey(state, 'strafeRight', true);
    hideHint(state.overlay);
  }

  function onKeyUp(e) {
    if (!state) return;
    var k = e.key.toLowerCase();
    if (e.key === 'ArrowUp' || k === 'w') setKey(state, 'forward', false);
    if (e.key === 'ArrowDown' || k === 's') setKey(state, 'back', false);
    if (e.key === 'ArrowLeft') setKey(state, 'left', false);
    if (e.key === 'ArrowRight') setKey(state, 'right', false);
    if (k === 'a') setKey(state, 'strafeLeft', false);
    if (k === 'd') setKey(state, 'strafeRight', false);
  }

  function bindDragLook(overlay, canvas) {
    var dragging = false, lastX = 0;

    function start(x) { dragging = true; lastX = x; hideHint(overlay); }
    function move(x) {
      if (!dragging || !state) return;
      var dx = x - lastX;
      lastX = x;
      state.player.a += dx * DRAG_SENSITIVITY;
    }
    function end() { dragging = false; }

    canvas.addEventListener('mousedown', function (e) { start(e.clientX); });
    window.addEventListener('mousemove', function (e) { move(e.clientX); });
    window.addEventListener('mouseup', end);

    canvas.addEventListener('touchstart', function (e) { start(e.touches[0].clientX); }, { passive: true });
    canvas.addEventListener('touchmove', function (e) { move(e.touches[0].clientX); }, { passive: true });
    canvas.addEventListener('touchend', end);

    state.dragCleanup = function () {
      // listeners are harmless once state is gone (move/end no-op without state),
      // kept simple since the overlay element itself is reused, not destroyed.
    };
  }

  function openGame() {
    var overlay = getOverlay();
    var canvas = overlay.querySelector('canvas');
    var ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = false;

    if (!skyTexCanvas) skyTexCanvas = buildSkyTexture();
    if (!grassTexData) grassTexData = buildGrassTexture();
    var skyData = skyTexCanvas.getContext('2d').getImageData(0, 0, SKY_TEX_W, HORIZON).data;

    var hint = overlay.querySelector('.wraith-game-hud__hint');
    if (hint) hint.classList.remove('is-hidden');

    state = {
      running: true,
      lastTs: 0,
      ctx: ctx,
      overlay: overlay,
      imgData: ctx.createImageData(RENDER_W, RENDER_H),
      player: { x: 2, z: 2, a: Math.PI * 0.15 },
      skyData: skyData,
      cloudDrift: 0,
      keys: { forward: false, back: false, left: false, right: false, strafeLeft: false, strafeRight: false }
    };

    overlay.classList.add('is-active');
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);
    bindDragLook(overlay, canvas);

    var closeBtn = overlay.querySelector('.wraith-game-close');
    closeBtn.onclick = closeGame;

    var touchButtons = overlay.querySelectorAll('.wraith-game-touch button');
    touchButtons.forEach(function (btn) {
      var act = btn.getAttribute('data-act');
      var mapped = act === 'left' ? 'left' : act === 'right' ? 'right' : act;
      var down = function (ev) { ev.preventDefault(); setKey(state, mapped, true); hideHint(overlay); };
      var up = function (ev) { ev.preventDefault(); setKey(state, mapped, false); };
      btn.addEventListener('touchstart', down, { passive: false });
      btn.addEventListener('touchend', up, { passive: false });
      btn.addEventListener('touchcancel', up, { passive: false });
      btn.addEventListener('mousedown', down);
      btn.addEventListener('mouseup', up);
      btn.addEventListener('mouseleave', up);
    });

    rafId = requestAnimationFrame(loop);
  }

  function closeGame() {
    var overlay = document.getElementById(OVERLAY_ID);
    if (overlay) overlay.classList.remove('is-active');
    if (state) state.running = false;
    if (rafId) cancelAnimationFrame(rafId);
    document.removeEventListener('keydown', onKeyDown);
    document.removeEventListener('keyup', onKeyUp);
    state = null;
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest && e.target.closest('[data-wraith-game-trigger]');
    if (trigger) {
      e.preventDefault();
      openGame();
    }
  });
})();
